import { useState } from "react";
import { KlauselKarte } from "./KlauselEditor";
import { ParagrafKopf } from "./ParagrafKopf";
import {
  fehlertext, useKlauselAendern, useKlauselAnlegen, useKlauselLoeschen, useOrdnung,
  useParagrafAendern,
} from "../hooks/useVertragstext";
import type { Blatt, Klausel, ParagrafGruppe } from "../hooks/useVertragstext";

/** Die Gliederung mit allen Schreibwegen — bewusst hier gebündelt, damit die
 *  Seite darüber nur noch Rahmen und Zustand ist. */
export default function Paragrafen({ werkId, blatt, gruppen, marken: markenListe, werkzeug, schreibbar }: {
  werkId: string;
  blatt: Blatt;
  gruppen: ParagrafGruppe[];
  marken: { schluessel: string; zeigt: string; dokument: Blatt }[];
  werkzeug?: Parameters<typeof KlauselKarte>[0]["werkzeug"];
  schreibbar: boolean;
}) {
  const aendern = useKlauselAendern(werkId);
  const loeschen = useKlauselLoeschen(werkId);
  const anlegen = useKlauselAnlegen(werkId);
  const ordnung = useOrdnung(werkId);
  const parAendern = useParagrafAendern(werkId);

  // Alle zu als Ausgangslage: vierzehn Paragrafen mit achtundsiebzig Absätzen
  // aufgeklappt sind eine Seite, auf der man scrollt statt arbeitet.
  const [offene, setOffene] = useState<Set<string>>(new Set());
  const schluessel = (g: ParagrafGruppe) => `${g.dokument}-${g.anhang || ""}-${g.paragraf}`;
  const umschalten = (g: ParagrafGruppe) => setOffene(vorher => {
    const neu = new Set(vorher);
    const k = schluessel(g);
    if (!neu.delete(k)) neu.add(k);
    return neu;
  });
  const alleOffen = gruppen.length > 0 && gruppen.every(g => offene.has(schluessel(g)));

  /** Verschieben schickt die **ganze** Liste neu, nicht die eine Änderung.
   *  Teilweise Umsortierungen ergeben Zwischenstände, in denen zwei Absätze
   *  dieselbe Position haben — und darauf verlässt sich hier alles. */
  function verschieben(gruppe: ParagrafGruppe, index: number, richtung: -1 | 1) {
    const neu = [...gruppe.klauseln];
    const ziel = index + richtung;
    if (ziel < 0 || ziel >= neu.length) return;
    [neu[index], neu[ziel]] = [neu[ziel], neu[index]];
    ordnung.mutate(neu.map((k, i) => ({
      id: k.id, paragraf: gruppe.paragraf, sortierung: i + 1,
      dokument: gruppe.dokument, anhang: gruppe.anhang,
    })));
  }

  function paragrafVerschieben(index: number, richtung: -1 | 1) {
    const eigene = gruppen.filter(g => !g.anhang);
    const ziel = index + richtung;
    if (ziel < 0 || ziel >= eigene.length) return;
    const reihe = [...eigene];
    [reihe[index], reihe[ziel]] = [reihe[ziel], reihe[index]];
    ordnung.mutate(reihe.flatMap((g, i) =>
      g.klauseln.map((k, j) => ({
        id: k.id, paragraf: i + 1, sortierung: j + 1, dokument: blatt, anhang: null,
      }))));
  }

  let eigeneIndex = -1;

  // Eine abgewiesene Änderung — doppelte Verweismarke, gesperrter Baustein,
  // freigegebene Fassung — muss man sehen. Sonst wirkt der Knopf tot, und man
  // tippt dieselbe Marke ein zweites Mal.
  const abgewiesen = [aendern.error, loeschen.error, anlegen.error, ordnung.error,
                      parAendern.error].find(Boolean);

  return (
    <>
      {abgewiesen && <div className="vt-fehler">{fehlertext(abgewiesen)}</div>}

      <div className="vt-gliederung-leiste">
        <span>
          {gruppen.filter(g => !g.anhang).length} Abschnitte
          {gruppen.some(g => g.anhang)
            ? ` · ${gruppen.filter(g => g.anhang).length} Altanhänge`
            : ""}
        </span>
        <button
          className="pf-linkbtn"
          onClick={() => setOffene(alleOffen ? new Set() : new Set(gruppen.map(schluessel)))}
        >
          {alleOffen ? "Alle zuklappen" : "Alle aufklappen"}
        </button>
      </div>

      {gruppen.map((g) => {
        if (!g.anhang) eigeneIndex += 1;
        const meinIndex = eigeneIndex;
        const offen = offene.has(schluessel(g));
        let absatz = 0;
        return (
          <div key={schluessel(g)} className={"pf-card vt-par" + (offen ? "" : " is-zu")}>
            <ParagrafKopf
              gruppe={g} offen={offen} onToggle={() => umschalten(g)}
              schreibbar={schreibbar}
              onTitel={t => parAendern.mutate({
                paragraf: g.paragraf, anhang: g.anhang, dokument: g.dokument, titel: t,
              })}
              onHoch={!g.anhang && meinIndex > 0
                ? () => paragrafVerschieben(meinIndex, -1) : undefined}
              onRunter={!g.anhang && meinIndex < gruppen.filter(x => !x.anhang).length - 1
                ? () => paragrafVerschieben(meinIndex, 1) : undefined}
            />
            {offen && g.klauseln.map((k: Klausel, i: number) => {
              if (k.art === "absatz" && !g.anhang) absatz += 1;
              return (
                <KlauselKarte
                  key={k.id}
                  klausel={k}
                  nummer={k.art === "absatz" && !g.anhang ? absatz : null}
                  marken={markenListe} werkzeug={werkzeug} schreibbar={schreibbar}
                  onAendern={patch => aendern.mutate({ id: k.id, patch })}
                  onLoeschen={() => loeschen.mutate(k.id)}
                  onHoch={i > 0 ? () => verschieben(g, i, -1) : undefined}
                  onRunter={i < g.klauseln.length - 1 ? () => verschieben(g, i, 1) : undefined}
                />
              );
            })}
            {offen && schreibbar && (
              <div className="vt-par-fuss">
                <button className="pf-btn is-ghost is-small" onClick={() => anlegen.mutate({
                  dokument: g.dokument, paragraf: g.paragraf, anhang: g.anhang,
                  paragraf_titel: g.titel, paragraf_stil: g.stil,
                  sortierung: g.klauseln.length + 1, art: "absatz", text: "",
                })}>＋ Absatz</button>
                <select className="vt-baustein-neu" value="" onChange={e => {
                  if (!e.target.value) return;
                  anlegen.mutate({
                    dokument: g.dokument, paragraf: g.paragraf, anhang: g.anhang,
                    paragraf_titel: g.titel, paragraf_stil: g.stil,
                    sortierung: g.klauseln.length + 1,
                    art: "baustein", baustein: e.target.value,
                  });
                }}>
                  <option value="">＋ Baustein…</option>
                  {(werkzeug?.bausteine || []).map(b => (
                    <option key={b.name} value={b.name}>{b.titel}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}
